import { useEffect, useId, useImperativeHandle, useMemo, useRef, useState, type KeyboardEvent, type Ref } from 'react'

import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'
import { scopedTranslation } from '@utils/I18n'

import type { FoundPlace } from '@utils/embeds/distance'
import { RADIUS_OPTIONS, type Radius } from '@utils/embeds/embedConfig'
import {
  firstPlaceFor,
  placeFromPrediction,
  useGoogleMaps,
  type Found,
  type PlaceAutocompleteElement,
  type PlaceSelectEvent,
} from '@utils/embeds/places'
import { allowsFeature } from '@utils/embeds/permissionsPolicy'
import { EmbedButton } from './EmbedButton'

const t = scopedTranslation('embeds.events.search')

function geolocationAllowed() {
  return 'geolocation' in navigator && allowsFeature('geolocation')
}

type Message = { key: 'not_found'; query: string } | { key: 'location_refused' | 'search_failed' | 'unavailable' }

export type LocationSearchHandle = { focus: () => void }

type LocationSearchProps = {
  label: string
  placeholder: string
  unavailableMessage: string
  failedMessage: string
  country: string
  googleMapsApiKey: string
  place: FoundPlace | null
  onPlaceChange: (place: FoundPlace | null) => void
  radius?: Radius
  onRadiusChange?: (radius: Radius) => void
  stacked: boolean
  ref?: Ref<LocationSearchHandle>
}

export function LocationSearch({
  label,
  placeholder,
  unavailableMessage,
  failedMessage,
  country,
  googleMapsApiKey,
  place,
  onPlaceChange,
  radius,
  onRadiusChange,
  stacked,
  ref,
}: LocationSearchProps) {
  const messageId = useId()
  const { isLoaded, loadError } = useGoogleMaps({ apiKey: googleMapsApiKey, country })
  const regionCodes = useMemo(() => [country.toLowerCase()], [country])
  const [searchMessage, setSearchMessage] = useState<Message | null>(null)
  const [locating, setLocating] = useState(false)
  const [canLocate] = useState(geolocationAllowed)
  const widget = useRef<PlaceAutocompleteElement>(null)
  const latestSearch = useRef(0)

  useImperativeHandle(ref, () => ({ focus: () => widget.current?.focus() }), [])

  useEffect(() => {
    if (!place && widget.current) widget.current.value = ''
  }, [place])

  const selectPlace = (found: FoundPlace, text: string) => {
    if (widget.current) widget.current.value = text
    onPlaceChange(found)
  }

  const search = async (query: string, find: () => Promise<Found>) => {
    const current = ++latestSearch.current
    setSearchMessage(null)
    try {
      const found = await find()
      if (current !== latestSearch.current) return
      if (found) selectPlace(found.place, found.label)
      else setSearchMessage({ key: 'not_found', query })
    } catch {
      if (current === latestSearch.current) setSearchMessage({ key: 'search_failed' })
    }
  }

  const searchPick = ({ placePrediction }: PlaceSelectEvent) =>
    search(placePrediction.text.text, async () => {
      const found = await placeFromPrediction(placePrediction)
      return found && { place: found, label: widget.current?.value || placePrediction.text.text }
    })

  const searchTypedOnEnter = (event: KeyboardEvent<PlaceAutocompleteElement>) => {
    if (event.key !== 'Enter') return
    const searchAtKeyDown = latestSearch.current
    setTimeout(() => {
      const input = widget.current?.value.trim()
      if (!input || searchAtKeyDown !== latestSearch.current) return
      void search(input, () => firstPlaceFor({ input, country }))
    })
  }

  const forgetPlaceIfEmpty = () => {
    if (widget.current?.value) return
    latestSearch.current++
    if (place) onPlaceChange(null)
  }

  const handleTextChange = () => {
    if (searchMessage?.key === 'not_found') setSearchMessage(null)
    forgetPlaceIfEmpty()
  }

  const locate = () => {
    if (locating || !isLoaded) return
    setSearchMessage(null)
    setLocating(true)
    const current = ++latestSearch.current
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setLocating(false)
        if (current !== latestSearch.current) return
        selectPlace(
          { lat: coords.latitude, lng: coords.longitude, name: t('your_location_in_sentence') },
          t('your_location')
        )
      },
      () => {
        setLocating(false)
        if (current === latestSearch.current) setSearchMessage({ key: 'location_refused' })
      },
      { maximumAge: 10 * 60 * 1000, timeout: 15 * 1000 }
    )
  }

  const showRadius = place && radius !== undefined && onRadiusChange
  const message: Message | null = searchMessage ?? (loadError ? { key: 'unavailable' } : null)
  const isError = message?.key === 'not_found'
  const messageText = (shown: Message) => {
    if (shown.key === 'not_found') return t('not_found', { query: shown.query })
    if (shown.key === 'unavailable') return unavailableMessage
    if (shown.key === 'search_failed') return failedMessage
    return t(shown.key)
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <div className={cn('tw:flex tw:gap-2', stacked ? 'tw:flex-col' : 'tw:flex-row tw:items-start')}>
        {isLoaded ? (
          <gmp-place-autocomplete
            ref={widget}
            aria-label={label}
            placeholder={placeholder}
            includedRegionCodes={regionCodes}
            className="tw:block tw:min-w-0 tw:flex-1 tw:rounded-(--embed-radius-sm,0.5rem) tw:bg-white tw:ring-1 tw:ring-gray-400"
            style={{ colorScheme: 'light', fontFamily: 'inherit' }}
            ongmp-select={searchPick}
            ongmp-error={() => setSearchMessage({ key: 'search_failed' })}
            onKeyDown={searchTypedOnEnter}
            onInput={handleTextChange}
            onClick={() => setTimeout(handleTextChange)}
          />
        ) : (
          <span className="tw:flex tw:h-12 tw:min-w-0 tw:flex-1 tw:items-center tw:gap-2 tw:rounded-(--embed-radius-sm,0.5rem) tw:border tw:border-gray-400 tw:bg-white tw:px-3">
            <Icon type="search-lg" className="tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-400" />
            <input
              type="text"
              aria-label={label}
              aria-describedby={message ? messageId : undefined}
              placeholder={placeholder}
              disabled
              className="tw:h-full tw:min-w-0 tw:flex-1 tw:border-0 tw:bg-transparent tw:p-0 tw:text-base tw:placeholder:text-gray-500 tw:disabled:cursor-not-allowed"
            />
          </span>
        )}
        {((canLocate && !loadError) || showRadius) && (
          <div className="tw:flex tw:gap-2">
            {canLocate && !loadError && (
              <EmbedButton
                color="neutral"
                outline
                size="lg"
                onClick={locate}
                aria-disabled={locating || !isLoaded || undefined}
              >
                <Icon type="navigation-pointer-01" className="tw:h-4 tw:w-4" />
                {locating ? t('locating') : t('use_my_location')}
              </EmbedButton>
            )}
            {showRadius && (
              <select
                aria-label={t('radius')}
                value={radius}
                onChange={(event) => onRadiusChange(Number(event.target.value) as Radius)}
                className="tw:h-12 tw:cursor-pointer tw:rounded-(--embed-radius-sm,0.5rem) tw:border tw:border-gray-300 tw:bg-white tw:px-2 tw:text-sm tw:text-gray-900 tw:focus-visible:outline tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2 tw:focus-visible:outline-(--embed-brand-ink,#111827)"
              >
                {RADIUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {t('within', { radius: option })}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>
      {message && (
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-x-3 tw:gap-y-2">
          <p
            id={messageId}
            role={isError ? 'alert' : 'status'}
            className={cn(
              'tw:m-0 tw:flex tw:items-start tw:gap-1.5 tw:text-sm',
              isError ? 'tw:text-error-700' : 'tw:text-gray-700'
            )}
          >
            <Icon type={isError ? 'alert-circle' : 'info-circle'} className="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0" />
            {messageText(message)}
          </p>
          {message.key === 'unavailable' && (
            <EmbedButton color="neutral" outline onClick={() => window.location.reload()}>
              {t('try_again')}
            </EmbedButton>
          )}
        </div>
      )}
    </div>
  )
}
